import { PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { AppPaths } from "@p0security/shared/routes/constants";
import type { BundleDefinition } from "@p0security/shared/types/bundle";
import type { Policy } from "@p0security/shared/types/policy/types";
import { Alert, App, Flex, Spacing, Spin, message } from "@p0security/ui";
import { isEqual } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AccessEditor } from "../Jit/Policy/components/editors/AccessEditor";
import { RequestorEditor } from "../Jit/Policy/components/editors/RequestorEditor";
import { InstalledIntegrationsProvider } from "../Jit/Policy/providers/InstalledIntegrationsProvider";
import {
  PolicyEditorContext,
  RoutingRuleEditorProvider,
} from "../Jit/Policy/store/RoutingRuleEditorContext";
import { SECTION_TITLES } from "../Policies/constants";
import { BundleResourceEditor } from "./BundleResourceEditor";
import { EditorHeader } from "./EditorHeader";
import { EditorStepper } from "./EditorStepper";
import type { ResourceMode } from "./ResourceEditor";
import {
  MAX_BUNDLE_ITEMS,
  MIN_BUNDLE_ITEMS,
  useAccessBundle,
} from "./useAccessBundles";
import { toPrefill } from "./utils/prefill";

const BundleEditorPageInner: React.FC<{
  initialBundle?: BundleDefinition;
  saveBundle: ReturnType<typeof useAccessBundle>["save"];
  bundleError?: string;
  onClearBundleError: () => void;
}> = ({ initialBundle, saveBundle, bundleError, onClearBundleError }) => {
  const navigate = useNavigate();
  const { orgSlug } = useParams();
  const location = useLocation();
  const { modal } = App.useApp();

  const {
    name,
    disabled,
    requestor,
    approval,
    setName,
    setDisabled,
    isComponentValid,
  } = useContext(PolicyEditorContext);

  const isEditMode = initialBundle !== undefined;

  const [savingBundle, setSavingBundle] = useState(false);
  const [accessRuleEditInProgress, setAccessRuleEditInProgress] =
    useState(false);

  const [bundleItems, setBundleItems] = useState(initialBundle?.items ?? []);

  const isBundleValid =
    bundleItems.length >= MIN_BUNDLE_ITEMS &&
    bundleItems.length <= MAX_BUNDLE_ITEMS;

  const isValid =
    isComponentValid("requestor") &&
    isBundleValid &&
    isComponentValid("approval");

  const hasFormChanges = useMemo(() => {
    if (!isEditMode) {
      return !!(
        name ||
        disabled ||
        bundleItems.length > 0 ||
        (requestor && !isEqual(requestor, { type: "any" })) ||
        (approval && approval.length > 0)
      );
    }
    return (
      name !== (initialBundle.label ?? "") ||
      (disabled ?? false) !== (initialBundle.disabled ?? false) ||
      !isEqual(bundleItems, initialBundle.items) ||
      !isEqual(requestor, initialBundle.requestor ?? { type: "any" }) ||
      !isEqual(approval, initialBundle.approval ?? [{ type: "p0" }])
    );
  }, [
    isEditMode,
    initialBundle,
    name,
    disabled,
    bundleItems,
    requestor,
    approval,
  ]);

  const isDirty = hasFormChanges;
  const isEditing = accessRuleEditInProgress;

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty || isEditing) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, isEditing]);

  const exitToStudio = useCallback(() => {
    const search = new URLSearchParams(location.search);
    search.set("tab", "bundles");
    navigate(`/o/${orgSlug}/${AppPaths.Policies}?${search.toString()}`);
  }, [navigate, orgSlug, location.search]);

  const handleCancel = useCallback(() => {
    if (isDirty || isEditing) {
      modal.confirm({
        title: "You have unsaved changes",
        content: "Are you sure you want to leave? Your changes will be lost.",
        okText: "Discard",
        okType: "danger",
        cancelText: "Keep editing",
        onOk: exitToStudio,
      });
    } else {
      exitToStudio();
    }
  }, [isDirty, isEditing, modal, exitToStudio]);

  const handlePolicyRedirect = useCallback(
    (resourceMode: ResourceMode) => {
      navigate(`/o/${orgSlug}/${AppPaths.Policies}/new`, {
        state: {
          prefill: { name, disabled, requestor, approval },
          resourceMode,
        },
      });
    },
    [navigate, orgSlug, name, disabled, requestor, approval]
  );

  const handleSave = useCallback(async () => {
    if (!isValid || !requestor || !approval) return;

    setSavingBundle(true);
    const label = name?.trim() || "";
    const saved = await saveBundle({
      label,
      items: bundleItems,
      requestor,
      approval,
      disabled: disabled ?? false,
    });
    setSavingBundle(false);
    if (!saved) return;
    const displayName = label || "Untitled bundle";
    message.success(
      `Bundle "${displayName}" was successfully ${isEditMode ? "saved" : "created"}.`
    );
    exitToStudio();
  }, [
    isValid,
    requestor,
    approval,
    name,
    bundleItems,
    saveBundle,
    disabled,
    isEditMode,
    exitToStudio,
  ]);

  return (
    <Spacing asChild mx="auto">
      <Flex
        vertical
        className="max-w-[min(1440px,calc(100vw-48px))] pb-8 w-full"
      >
        <EditorHeader
          name={name ?? ""}
          disabled={disabled ?? false}
          isDirty={isDirty}
          isValid={isValid}
          isSubmitting={savingBundle}
          onNameChange={setName}
          onDisabledChange={setDisabled}
          onSave={handleSave}
          onCancel={handleCancel}
          namePlaceholder="Bundle name (optional)"
          untitledLabel="Untitled bundle"
          saveIcon={isEditMode ? <SaveOutlined /> : <PlusOutlined />}
          saveLabel={isEditMode ? "Save Access Bundle" : "Create Access Bundle"}
          enabledTooltip="Toggle bundle. Disabled bundles are not available for requests."
          saveTooltip={
            !isBundleValid
              ? `Add at least ${MIN_BUNDLE_ITEMS} resources to the bundle.`
              : "Complete all sections before saving."
          }
        />

        {bundleError && (
          <Spacing asChild mb={16}>
            <div>
              <Alert
                message="Error"
                description={bundleError}
                type="error"
                showIcon
                closable
                onClose={onClearBundleError}
              />
            </div>
          </Spacing>
        )}

        <Flex vertical gap={16}>
          <EditorStepper
            sections={[
              {
                title: SECTION_TITLES.requestor,
                valid: isComponentValid("requestor"),
                touched: isEditMode || requestor !== undefined,
                children: <RequestorEditor />,
              },
              {
                title: SECTION_TITLES.resource,
                valid: isBundleValid,
                touched: isEditMode || bundleItems.length >= MIN_BUNDLE_ITEMS,
                children: (
                  <BundleResourceEditor
                    items={bundleItems}
                    onItemsChange={setBundleItems}
                    onPolicyRedirect={handlePolicyRedirect}
                  />
                ),
              },
              {
                title: SECTION_TITLES.approval,
                valid: isComponentValid("approval"),
                touched: approval.length > 0,
                children: (
                  <AccessEditor
                    onAccessRuleEditInProgress={setAccessRuleEditInProgress}
                    defaultOpen
                  />
                ),
              },
            ]}
          />
        </Flex>
      </Flex>
    </Spacing>
  );
};

const bundleToInitialRule = (bundle: BundleDefinition): Policy => ({
  name: bundle.label,
  disabled: bundle.disabled ?? false,
  requestor: bundle.requestor ?? { type: "any" },
  resource: {
    type: "integration" as const,
    service: "bundle",
    accessType: "any",
    filters: {},
  },
  approval: bundle.approval ?? [{ type: "p0" as const }],
});

export const BundleEditorPage: React.FC = () => {
  const { bundleId } = useParams<{ bundleId?: string }>();
  const location = useLocation();

  const {
    bundle,
    loading: bundleLoading,
    error: bundleError,
    clearError: clearBundleError,
    save: saveBundle,
  } = useAccessBundle(bundleId);

  if (bundleLoading) {
    return (
      <Spacing asChild mt={64}>
        <Spin size="large" className="flex justify-center" />
      </Spacing>
    );
  }

  if (bundleId && !bundle && !bundleLoading) {
    return (
      <Spacing asChild m={24}>
        <Alert
          type="error"
          message="Bundle not found"
          description="The bundle you are trying to edit does not exist. It may have been deleted."
          showIcon
        />
      </Spacing>
    );
  }

  const prefill = toPrefill(location.state);

  const initialRule = bundle
    ? bundleToInitialRule(bundle)
    : prefill
      ? {
          name: prefill.name,
          disabled: prefill.disabled ?? false,
          requestor: prefill.requestor ?? { type: "any" as const },
          resource: {
            type: "integration" as const,
            service: "bundle",
            accessType: "any",
            filters: {},
          },
          approval: prefill.approval ?? [],
        }
      : undefined;

  return (
    <InstalledIntegrationsProvider>
      <RoutingRuleEditorProvider
        key={bundleId ?? "new"}
        initialRule={initialRule}
      >
        <BundleEditorPageInner
          initialBundle={bundle}
          saveBundle={saveBundle}
          bundleError={bundleError}
          onClearBundleError={clearBundleError}
        />
      </RoutingRuleEditorProvider>
    </InstalledIntegrationsProvider>
  );
};
